import { useState, type FormEvent } from 'react';
import { AlertCircle, Info, Loader2, Play, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTenantId } from '@/lib/auth/hooks';
import { CubixApiError } from '@/lib/api/types';
import { cn } from '@/lib/utils';

import { AnalysisSkeleton } from '@/components/shared/AnalysisSkeleton';

import { useLatestDuration, useTriggerDuration } from './api';
import type { DurationAnalysisDetail, ScenarioCode } from './types';

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Phase 2.0.DURATION — Modified Duration + DV01 analysis page.
 *
 * Six headline metric tiles:
 *   Row 1 — Modified Duration  | Stressed Duration  | Duration Delta   [years]
 *   Row 2 — DV01               | Stressed DV01      | DV01 Delta       [CCY/bp]
 *
 * Plus a secondary portfolio-PV section showing the denominator the
 * duration numbers are derived from, and PV shift under stress.
 *
 * Degraded banner appears when the engine fell back to placeholder rates
 * (same contract as the NIM degraded banner).
 */
export function DurationAnalysisPage() {
  const tenantId = useTenantId();
  const [date, setDate] = useState<string>(todayISO());
  const [scenarioCode, setScenarioCode] = useState<ScenarioCode>('PARALLEL_UP_200');

  const latest = useLatestDuration(date);
  const trigger = useTriggerDuration();

  const detail: DurationAnalysisDetail | null = latest.data ?? null;

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    trigger.mutate({ calculationDate: date, scenarioCode, force: true });
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Modified Duration &amp; DV01</h1>
        <p className="text-sm text-muted-foreground">
          Interest-rate sensitivity of the cashflow portfolio — tenant{' '}
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{tenantId ?? '—'}</code>.
        </p>
      </header>

      <DegradedBanner detail={detail} />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Run Duration</CardTitle>
          <CardDescription>
            Retrieves the persisted result for the chosen date, or runs a fresh
            calculation under the selected scenario. All future cashflows are
            included — no horizon cutoff.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid gap-3 md:grid-cols-3" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="dur-date">Calculation date</Label>
              <Input
                id="dur-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dur-scenario">Stress scenario</Label>
              <select
                id="dur-scenario"
                value={scenarioCode}
                onChange={(e) => setScenarioCode(e.target.value as ScenarioCode)}
                className={cn(
                  'flex h-9 w-full rounded-md border bg-background px-2 text-sm',
                  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                )}
              >
                <option value="FLAT">Flat (no shock)</option>
                <option value="PARALLEL_UP_100">Parallel +100bp</option>
                <option value="PARALLEL_UP_200">Parallel +200bp</option>
                <option value="PARALLEL_UP_300">Parallel +300bp</option>
                <option value="PARALLEL_DOWN_100">Parallel −100bp</option>
                <option value="PARALLEL_DOWN_200">Parallel −200bp</option>
              </select>
            </div>
            <div className="flex items-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => latest.refetch()}
                disabled={latest.isFetching}
              >
                {latest.isFetching ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Reload
              </Button>
              <Button type="submit" disabled={trigger.isPending}>
                {trigger.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Play className="mr-2 h-4 w-4" />
                )}
                Run on-demand
              </Button>
            </div>
          </form>

          {trigger.isError ? <ApiErrorBanner error={trigger.error} /> : null}
          {trigger.isSuccess ? (
            <p className="mt-3 text-sm text-muted-foreground">
              Calculation complete · result_id{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">
                {trigger.data.resultId}
              </code>
              {' · '}engine {trigger.data.engineVersion}
            </p>
          ) : null}
        </CardContent>
      </Card>

      {latest.isError ? <ApiErrorBanner error={latest.error} /> : null}

      {latest.isLoading && !detail ? (
        <AnalysisSkeleton />
      ) : (
        <>
          <DurationTiles detail={detail} loading={latest.isLoading} />
          <PvSection detail={detail} loading={latest.isLoading} />
        </>
      )}
    </div>
  );
}

// ── Degraded banner ──────────────────────────────────────────────────────────

function DegradedBanner({ detail }: { detail: DurationAnalysisDetail | null }) {
  if (!detail || !detail.degradedMode) return null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-800">
      <Info className="mt-0.5 h-4 w-4 shrink-0" />
      <p>
        <strong>Degraded mode — placeholder rates used.</strong> No validated
        yield-curve snapshot was available for {detail.calculationDate}, so
        the engine fell back to the placeholder constants from{' '}
        <code>/settings/alm-parameters</code>. Upload + validate a curve on
        the <em>Parameters → Yield Curves</em> page to get curve-aware Duration.
      </p>
    </div>
  );
}

// ── Duration metric tiles (MD + DV01) ─────────────────────────────────────

function DurationTiles({
  detail,
  loading,
}: {
  detail: DurationAnalysisDetail | null;
  loading: boolean;
}) {
  const scenario = detail?.scenarioCode ?? '—';
  const deltaWarn = detail && Number(detail.durationDelta) > 0;
  const dv01DeltaWarn = detail && Number(detail.dv01Delta) > 0;

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Modified Duration [years]
      </p>
      <div className="grid gap-4 md:grid-cols-3">
        <Tile
          title="MD Baseline"
          value={detail ? formatYears(detail.modifiedDuration) : '—'}
          hint={loading ? 'Loading…' : detail ? 'No-shock scenario' : 'No result yet'}
        />
        <Tile
          title="MD Stressed"
          value={detail ? formatYears(detail.stressedDuration) : '—'}
          hint={detail ? `Scenario: ${scenario}` : '—'}
        />
        <Tile
          title="MD Delta"
          value={detail ? formatYears(detail.durationDelta) : '—'}
          hint={
            detail
              ? deltaWarn
                ? 'Duration extends under shock — higher rate sensitivity'
                : 'Duration shortens under shock'
              : '—'
          }
          tone={deltaWarn ? 'warn' : undefined}
        />
      </div>

      <p className="pt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        DV01 — Dollar Value of 1bp [CCY / bp]
      </p>
      <div className="grid gap-4 md:grid-cols-3">
        <Tile
          title="DV01 Baseline"
          value={detail ? formatCcy(detail.dv01) : '—'}
          hint={loading ? 'Loading…' : detail ? 'CCY loss per +1bp parallel shift' : 'No result yet'}
        />
        <Tile
          title="DV01 Stressed"
          value={detail ? formatCcy(detail.stressedDv01) : '—'}
          hint={detail ? `Scenario: ${scenario}` : '—'}
        />
        <Tile
          title="DV01 Delta"
          value={detail ? formatCcy(detail.dv01Delta) : '—'}
          hint={
            detail
              ? dv01DeltaWarn
                ? 'Sensitivity increases under shock'
                : 'Sensitivity decreases under shock'
              : '—'
          }
          tone={dv01DeltaWarn ? 'warn' : undefined}
        />
      </div>
    </div>
  );
}

// ── PV context section ────────────────────────────────────────────────────

function PvSection({
  detail,
  loading,
}: {
  detail: DurationAnalysisDetail | null;
  loading: boolean;
}) {
  if (!detail && !loading) return null;
  const pvShiftNeg = detail && Number(detail.pvShift) < 0;
  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Portfolio Present Value [CCY]
      </p>
      <div className="grid gap-4 md:grid-cols-3">
        <Tile
          title="Portfolio PV"
          value={detail ? formatCcy(detail.portfolioPv) : '—'}
          hint={loading ? 'Loading…' : detail ? 'Net PV — assets minus liabilities' : '—'}
        />
        <Tile
          title="Stressed PV"
          value={detail ? formatCcy(detail.stressedPv) : '—'}
          hint={detail ? `Under ${detail.scenarioCode}` : '—'}
        />
        <Tile
          title="PV Shift"
          value={detail ? formatCcy(detail.pvShift) : '—'}
          hint={
            detail
              ? pvShiftNeg
                ? 'Portfolio loses value under shock'
                : 'Portfolio gains value under shock'
              : '—'
          }
          tone={pvShiftNeg ? 'warn' : undefined}
        />
      </div>
    </div>
  );
}

// ── Shared tile ───────────────────────────────────────────────────────────

function Tile({
  title,
  value,
  hint,
  tone,
}: {
  title: string;
  value: string;
  hint?: string;
  tone?: 'warn';
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle className={cn('text-2xl', tone === 'warn' && 'text-destructive')}>
          {value}
        </CardTitle>
      </CardHeader>
      {hint ? (
        <CardContent className="pt-0 text-xs text-muted-foreground">{hint}</CardContent>
      ) : null}
    </Card>
  );
}

// ── Formatters ────────────────────────────────────────────────────────────

/** Format a decimal-string duration value to 4 decimal places [years]. */
function formatYears(decimal: string): string {
  const n = Number(decimal);
  if (!Number.isFinite(n)) return decimal;
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(4)} yr`;
}

/**
 * Format a large currency amount compactly.
 * e.g. 12345.67800000 → "+12,345.68"
 *      −98500000.00 → "−98.5M"
 */
function formatCcy(decimal: string): string {
  const n = Number(decimal);
  if (!Number.isFinite(n)) return decimal;
  const abs = Math.abs(n);
  const sign = n < 0 ? '−' : n > 0 ? '+' : '';
  if (abs >= 1_000_000) {
    return `${sign}${(abs / 1_000_000).toFixed(2)}M`;
  }
  if (abs >= 1_000) {
    return `${sign}${(abs / 1_000).toFixed(2)}K`;
  }
  return `${sign}${abs.toFixed(2)}`;
}

// ── Error banner ──────────────────────────────────────────────────────────

function ApiErrorBanner({ error }: { error: CubixApiError | Error }) {
  const isCubix = error instanceof CubixApiError;
  return (
    <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        <p className="font-medium">{error.message}</p>
        {isCubix ? (
          <p className="mt-0.5 text-xs opacity-80">
            code{' '}
            <code className="rounded bg-background/60 px-1 py-0.5">{error.code}</code>
            {error.field ? <> · field {error.field}</> : null}
          </p>
        ) : null}
      </div>
    </div>
  );
}
